import { AnyAction } from "redux";
import { call, fork, put, select, take, takeEvery, takeLatest } from "redux-saga/effects";

import { bridge } from "@multiweb/reactCore/bridge";
import { createLoadLocalizationKeysAction } from "@multiweb/reactCore/i18n";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import {
    createLoadWorkflowEntitiesAction,
    createSetTouchedAction,
    createShowMessageWindowAction,
    createUpdateActivityAction,
    createUpdateTransitionAction,
    createUpdateWorkflowViewAction,
} from "./actions";
import {
    CREATE_TRANSITION,
    DELETE_SELECTED_WF_ENTITIES,
    DELETE_SET,
    FETCHING_KEY_WFEDITOR,
    LOAD_ACTIVITY_HEADER,
    LOAD_TRANSITION_HEADER,
    SET_TOUCHED,
    UPDATE_WORKFLOW_VIEW,
    WFSTAV_ATTRIBUTES,
} from "./constants";
import { LOC_CANCEL, LOC_DELETE, LOC_DELETE_SELECTED_ELEMENTS, LOC_DELETE_SET, LOC_KEYS_WF_EDITOR } from "./locKeys";
import {
    createGetOrderedSets,
    createGetSelectedTransitions,
    getActivityByIndex,
    getSelectedActivities,
    getSelectedSetId,
    getSelectedSetIndex,
    getTransitionByIndex,
    isTouched,
    isWorkflowReadOnly,
} from "./selectors";
import {
    ICreateTransitionAction,
    IDeleteSelectedWfEntitiesAction,
    IDeleteSetAction,
    IDialogConfirmedAction,
    ILoadActivityHeaderAction,
    ILoadTransitionHeaderAction,
    ISetTouchedAction,
    IUpdateWorkflowViewAction,
} from "./types";
import { UiActivity } from "./UiActivity";
import { createDialogUserResponsePattern, getUiWorkflowMutableObject } from "./utils";

const { errorHandler, generateUUID, getDataReaderClient } = bridge;

// Nutné exportovat kvůli testům
export const getSelectedTransitions = createGetSelectedTransitions();
export const getOrderedSets = createGetOrderedSets();

export const saga: SagaWithReturnType = function* workflowEditorSaga() {
    yield fork(loadLocalizationsSaga);
    yield takeLatest(UPDATE_WORKFLOW_VIEW, updateWorkflowViewSaga);
    yield takeEvery(SET_TOUCHED, setTouchedSaga);
    yield takeLatest(CREATE_TRANSITION, handleCreateTransitionSaga);
    yield takeLatest(DELETE_SELECTED_WF_ENTITIES, handleDeleteSelectedWfEntitiesSaga);
    yield takeLatest(DELETE_SET, handleDeleteSetSaga);
};

/**
 * Načte lokalizace.
 */
export const loadLocalizationsSaga: SagaWithReturnType = function* loadLocalizationsSaga() {
    yield put(createLoadLocalizationKeysAction(LOC_KEYS_WF_EDITOR, FETCHING_KEY_WFEDITOR));
};

/**
 * Obstarává logiku přidání propojení u aktivit.
 * @param action
 */
export const handleCreateTransitionSaga: SagaWithReturnType<[ICreateTransitionAction]> = function* handleCreateTransitionSaga(action) {
    const { hostKey } = action.meta;

    const selectedActivities: SagaYieldReturn<typeof getSelectedActivities> = yield select(getSelectedActivities, hostKey);
    const isReadOnly: SagaYieldReturn<typeof isWorkflowReadOnly> = yield select(isWorkflowReadOnly, hostKey);

    if (selectedActivities.length === 0 || isReadOnly)
        return;

    const uiWorkflow: SagaYieldReturn<typeof getUiWorkflowMutableObject> = yield call(getUiWorkflowMutableObject, hostKey);
    const selectedSetId: SagaYieldReturn<typeof getSelectedSetId> = yield select(getSelectedSetId, hostKey);

    let addedTransitionId: string | undefined;

    if (selectedActivities.length === 1) {
        const uiActivity = uiWorkflow.picassoModel.components[selectedActivities[0].picassoIndex] as UiActivity;
        addedTransitionId = uiWorkflow.workflow.addTransition(uiActivity.activity.id, uiActivity.activity.id, selectedSetId);
    } else {
        for (let i = 0; i < selectedActivities.length - 1; i++) {
            const fromActivity = uiWorkflow.picassoModel.components[selectedActivities[i].picassoIndex] as UiActivity;
            const toActivity = uiWorkflow.picassoModel.components[selectedActivities[i + 1].picassoIndex] as UiActivity;
            addedTransitionId = uiWorkflow.workflow.addTransition(fromActivity.activity.id, toActivity.activity.id, selectedSetId);
        }
    }

    yield put(createUpdateWorkflowViewAction(hostKey, selectedSetId, "", addedTransitionId));
};

/**
 * Obstarává logiku mazání zvolených prvků WF a čeká na akci uživatelského potvrzení smazání.
 * @param action
 */
export const handleDeleteSelectedWfEntitiesSaga: SagaWithReturnType<[IDeleteSelectedWfEntitiesAction]> = function* handleDeleteSelectedWfEntitiesSaga(action) {
    const { hostKey } = action.meta;

    const selectedActivities: SagaYieldReturn<typeof getSelectedActivities> = yield select(getSelectedActivities, hostKey);
    const selectedTransitions: SagaYieldReturn<typeof getSelectedTransitions> = yield select(getSelectedTransitions, hostKey);
    const isReadOnly: SagaYieldReturn<typeof isWorkflowReadOnly> = yield select(isWorkflowReadOnly, hostKey);

    if (isReadOnly || (selectedActivities.length === 0 && selectedTransitions.length === 0))
        return;

    const actionId: SagaYieldReturn<typeof generateUUID> = yield call(generateUUID);
    const dialogUserResponsePattern: SagaYieldReturn<typeof createDialogUserResponsePattern> = yield call(createDialogUserResponsePattern, actionId);

    yield put(createShowMessageWindowAction(hostKey, LOC_DELETE, LOC_CANCEL, actionId, LOC_DELETE_SELECTED_ELEMENTS));

    const responseAction: AnyAction = yield take(dialogUserResponsePattern);

    if (!(responseAction instanceof IDialogConfirmedAction))
        return;

    const { workflow, picassoModel }: SagaYieldReturn<typeof getUiWorkflowMutableObject> = yield call(getUiWorkflowMutableObject, hostKey);
    const selectedSetId: SagaYieldReturn<typeof getSelectedSetId> = yield select(getSelectedSetId, hostKey);
    const deleteTransition = workflow.deleteTransition.bind(workflow);
    const deleteActivity = workflow.deleteActivity.bind(workflow);

    for (const selectedTransition of selectedTransitions) {
        const component = picassoModel.components[selectedTransition.fromIndex] as UiActivity;
        yield call(deleteTransition, component.activity.id, selectedTransition.id);
    }

    for (const selectedActivity of selectedActivities) {
        yield call(deleteActivity, selectedActivity.id, selectedSetId);
    }

    yield put(createUpdateWorkflowViewAction(hostKey, selectedSetId));
};

/**
 * Obstarává logiku mazání sady a čeká na akci uživatelského potvrzení smazání.
 * @param action
 */
export const handleDeleteSetSaga: SagaWithReturnType<[IDeleteSetAction]> = function* handleDeleteSetSaga(action) {
    const { hostKey } = action.meta;

    const sets: SagaYieldReturn<typeof getOrderedSets> = yield select(getOrderedSets, hostKey);

    if (sets.length === 1)
        return;

    const actionId: SagaYieldReturn<typeof generateUUID> = yield call(generateUUID);
    const dialogUserResponsePattern: SagaYieldReturn<typeof createDialogUserResponsePattern> = yield call(createDialogUserResponsePattern, actionId);

    yield put(createShowMessageWindowAction(hostKey, LOC_DELETE, LOC_CANCEL, actionId, LOC_DELETE_SET));

    const responseAction: AnyAction = yield take(dialogUserResponsePattern);

    if (!(responseAction instanceof IDialogConfirmedAction))
        return;

    const { workflow }: SagaYieldReturn<typeof getUiWorkflowMutableObject> = yield call(getUiWorkflowMutableObject, hostKey);
    const selectedSetId: SagaYieldReturn<typeof getSelectedSetId> = yield select(getSelectedSetId, hostKey);
    const selectedSetIndex: SagaYieldReturn<typeof getSelectedSetIndex> = yield select(getSelectedSetIndex, hostKey);
    const removeSet = workflow.removeSet.bind(workflow);

    const previousSetIndex = selectedSetIndex - 1;
    const newSelectedSetIndex = previousSetIndex < 0 ? 1 : previousSetIndex;

    yield call(removeSet, selectedSetId);
    yield put(createUpdateWorkflowViewAction(hostKey, sets[newSelectedSetIndex].id));
};

/**
 * Updatuje aktivity/propojení/sady workflow a zobrazí je přes picasso.
 * @param action
 */
export const updateWorkflowViewSaga: SagaWithReturnType<[IUpdateWorkflowViewAction]> = function* updateWorkflowViewSaga(action) {
    const { hostKey } = action.meta;
    const { setId, selectedActivityId, selectedTransitionId, isNonEditChange } = action.payload;
    const uiWorkflow: SagaYieldReturn<typeof getUiWorkflowMutableObject> = yield call(getUiWorkflowMutableObject, hostKey);

    uiWorkflow.updatePicassoModel(setId);
    const { sets, activities, transitions, definitionParticipants } = uiWorkflow.workflowModel;

    const selectedComponent = activities.find(activity => activity.id === selectedActivityId);
    const selectedLink = transitions.find(transition => transition.id === selectedTransitionId);
    const touched: SagaYieldReturn<typeof isTouched> = yield select(isTouched, hostKey);

    if (!isNonEditChange && !touched)
        yield put(createSetTouchedAction(hostKey, true));

    yield put(createLoadWorkflowEntitiesAction(hostKey, sets, activities, transitions, uiWorkflow.picassoModel.components, uiWorkflow.picassoModel.links, definitionParticipants, setId, selectedComponent?.picassoIndex, selectedLink?.picassoIndex));
    yield takeEvery(LOAD_ACTIVITY_HEADER, loadActivityHeaderValueSaga);
    yield takeEvery(LOAD_TRANSITION_HEADER, loadTransitionHeaderValueSaga);
};

/**
 * Načte hlavičku pro aktivitu.
 * @param action
 */
export const loadActivityHeaderValueSaga: SagaWithReturnType<[ILoadActivityHeaderAction]> = function* loadActivityHeaderValueSaga(action) {
    const { hostKey } = action.meta;
    const { picassoIndex, wfStateCodes } = action.payload;

    try {
        const header: SagaYieldReturn<typeof getHeaderSaga> = yield call(getHeaderSaga, wfStateCodes);
        const activity: SagaYieldReturn<typeof getActivityByIndex> = yield select(getActivityByIndex, hostKey, picassoIndex);

        yield put(createUpdateActivityAction(hostKey, picassoIndex, { ...activity, header }));
    } catch (error) {
        yield call(errorHandler, error);
    }
};

/**
 * Načte hlavičku pro propojení.
 * @param action
 */
export const loadTransitionHeaderValueSaga: SagaWithReturnType<[ILoadTransitionHeaderAction]> = function* loadTransitionHeaderValueSaga(action) {
    const { hostKey } = action.meta;
    const { picassoIndex, wfStateCodes } = action.payload;

    try {
        const header: SagaYieldReturn<typeof getHeaderSaga> = yield call(getHeaderSaga, wfStateCodes);
        const transition: SagaYieldReturn<typeof getTransitionByIndex> = yield select(getTransitionByIndex, hostKey, picassoIndex);
        yield put(createUpdateTransitionAction(hostKey, picassoIndex, { ...transition, header }));
    } catch (error) {
        yield call(errorHandler, error);
    }
};

/**
 * Načte hodnoty stavů ze třídy WFStavy a konkatenuje je do stringu.
 * @param wfStateCodes Kódy stavů, jejichž hodnoty se mají načíst.
 * @returns String ve tvaru 'hodnota1 | hodnota2 | ... | hodnotaN'.
 */
export const getHeaderSaga: SagaWithReturnType<[readonly string[]], string> = function* getHeaderSaga(wfStateCodes) {
    const dataReaderClient: SagaYieldReturn<typeof getDataReaderClient> = yield call(getDataReaderClient);
    const getDataRowsWithParams = dataReaderClient.getDataRowsWithParams.bind(dataReaderClient);
    const wfStateDataRows: SagaYieldReturn<typeof getDataRowsWithParams> = yield call(getDataRowsWithParams, "WFStavy", WFSTAV_ATTRIBUTES, `kod in ['${wfStateCodes.join("','")}']`);

    const wfStateValues = wfStateDataRows.map(element => element["Stav_dokladu"].toString().trim());
    const concatenatedHeader = wfStateValues.join(" | ");

    return concatenatedHeader;
};

/**
 * Postará se o informování formuláře editoru WF o tom, že byla provedena editace.
 */
export const setTouchedSaga: SagaWithReturnType<[ISetTouchedAction]> = function* setTouchedSaga(action) {
    const { hostKey } = action.meta;
    const { touched } = action.payload;

    try {
        const uiWorkflow: SagaYieldReturn<typeof getUiWorkflowMutableObject> = yield call(getUiWorkflowMutableObject, hostKey);
        yield call(uiWorkflow.setIsWfEdited, touched);
    } catch (error) {
        yield call(errorHandler, error);
    }
};
